import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Play, Upload } from 'lucide-react';
import AccuracyGuidelineChatSidebar from '../components/AccuracyGuidelineChatSidebar';
import { AccuracyMetricsSection } from '../components/AccuracyMetricsSection';
import { GuidelineDraftEditor } from '../components/GuidelineDraftEditor';
import {
  buildDrilldownPath,
  buildTuningDrilldownPath,
  type MetricCountDrilldown,
} from '../services/accuracyCountDrilldown';
import type { EvaluationHistoryRow } from '../services/accuracyEvaluationHistory';
import {
  createEvaluationRun as createEvaluationRunApi,
  publishTuningSession as publishTuningSessionApi,
  putGuidelineDraft as putGuidelineDraftApi,
} from '../services/accuracyBackendApi';
import { applyProposalToDraftContent } from '../services/guidelineDraftApply';
import { buildTuningDraftChatContext } from '../services/accuracyTuningDraftChat';
import {
  loadSessionEditorSeed,
  type DraftCompareTargetId,
} from '../services/accuracyTuningDraftSeed';
import { buildAccuracyReportDetailPath } from '../services/accuracyReportPaths';
import type { EvaluationRun, GuidelineDraftContent, TuningSession } from '../types/accuracy';
import type { GuidelineChatProposal } from '../types/guidelineProposal';
import '../pages/AccuracyCountDrilldownPage.css';
import '../pages/AccuracyReportPage.css';
import '../pages/AccuracyTuningPages.css';

const AccuracyTuningSessionPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftDirty, setDraftDirty] = useState(false);
  const [publishRunId, setPublishRunId] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [session, setSession] = useState<TuningSession | null>(null);
  const [draft, setDraft] = useState<GuidelineDraftContent | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [runs, setRuns] = useState<EvaluationRun[]>([]);
  const [reportName, setReportName] = useState<string | null>(null);
  const [baselineReportId, setBaselineReportId] = useState<string | null>(null);
  const [compareTargets, setCompareTargets] = useState<
    Awaited<ReturnType<typeof loadSessionEditorSeed>>['compareTargets']
  >([]);
  const [compareTargetId, setCompareTargetId] = useState<DraftCompareTargetId>('baseline');
  const [chatErrors, setChatErrors] = useState<
    Awaited<ReturnType<typeof loadSessionEditorSeed>>['chatErrors']
  >([]);
  const [errorSourceLabel, setErrorSourceLabel] = useState('ベースライン評価');
  const [reportForChat, setReportForChat] = useState<
    Awaited<ReturnType<typeof loadSessionEditorSeed>>['report'] | null
  >(null);
  const lastSavedDraftRef = useRef<string>('');

  useEffect(() => {
    if (!sessionId) return;
    (async () => {
      setLoading(true);
      try {
        const seed = await loadSessionEditorSeed(sessionId);
        setSession(seed.session);
        setDraft(seed.draft);
        setDraftId(seed.draftId);
        setRuns(seed.runs);
        setReportName(seed.report.name);
        setBaselineReportId(seed.report.id);
        setCompareTargets(seed.compareTargets);
        setCompareTargetId(seed.compareTargets[0]?.id ?? 'baseline');
        setChatErrors(seed.chatErrors);
        setErrorSourceLabel(seed.errorSourceLabel);
        setReportForChat(seed.report);
        lastSavedDraftRef.current = JSON.stringify(seed.draft);
        setDraftDirty(false);
      } catch {
        setSession(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [sessionId, refresh]);

  const chatContext = useMemo(() => {
    if (!reportForChat || !draft) return null;
    return buildTuningDraftChatContext({
      report: reportForChat,
      errors: chatErrors,
      errorSourceLabel,
    });
  }, [reportForChat, draft, chatErrors, errorSourceLabel]);

  const handleDraftChange = useCallback((content: GuidelineDraftContent) => {
    setDraft(content);
    setDraftDirty(JSON.stringify(content) !== lastSavedDraftRef.current);
  }, []);

  const handleApplyProposal = useCallback((proposal: GuidelineChatProposal) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = applyProposalToDraftContent(prev, proposal);
      setDraftDirty(JSON.stringify(next) !== lastSavedDraftRef.current);
      return next;
    });
  }, []);

  const handleSaveDraft = useCallback(async () => {
    if (!draft || !draftId || saving || session?.status === 'published') return;
    setSaving(true);
    setMessage(null);
    try {
      await putGuidelineDraftApi({ draftId, content_json: draft });
      lastSavedDraftRef.current = JSON.stringify(draft);
      setDraftDirty(false);
      setMessage('ドラフトを保存しました');
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'ドラフトの保存に失敗しました');
    } finally {
      setSaving(false);
    }
  }, [draft, draftId, saving, session?.status]);

  const handleRunEvaluation = useCallback(async () => {
    if (!session || session.status === 'published') return;
    if (draftDirty) {
      setMessage('先にドラフトを保存してください');
      return;
    }
    setRunning(true);
    setMessage(null);
    try {
      const run = await createEvaluationRunApi(session.id);
      setRefresh((n) => n + 1);
      setMessage(`評価 #${run.run_number} を enqueue しました`);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : '評価に失敗しました');
    } finally {
      setRunning(false);
    }
  }, [session, draftDirty]);

  const handlePublish = useCallback(async () => {
    if (!session || !publishRunId) return;
    setMessage(null);
    try {
      const updated = await publishTuningSessionApi({
        sessionId: session.id,
        evaluation_run_id: publishRunId,
      });
      setMessage(`本番ガイドライン v${updated.published_guideline_version} として公開しました`);
      setRefresh((n) => n + 1);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : '公開に失敗しました');
    }
  }, [session, publishRunId]);

  /** Rows for this session only — matches the report detail 精度 table, without baseline. */
  const sessionHistoryRows = useMemo((): EvaluationHistoryRow[] => {
    if (!session || !baselineReportId) return [];
    const rows: EvaluationHistoryRow[] = runs.map((r) => ({
      id: `tuning:${r.id}`,
      kind: 'tuning',
      label: session.name,
      context: reportName ? `ベースライン: ${reportName}` : undefined,
      status: r.status,
      created_at: r.created_at,
      result: r.result,
      baseline_report_id: baselineReportId,
      tuning_session_id: session.id,
      evaluation_run_id: r.id,
      run_number: r.run_number,
    }));
    if (rows.length === 0) {
      rows.push({
        id: `tuning-session:${session.id}`,
        kind: 'tuning',
        label: session.name,
        context: 'ドラフト作成済み · 評価未実行',
        status: 'active',
        created_at: session.created_at,
        baseline_report_id: baselineReportId,
        tuning_session_id: session.id,
      });
    }
    return rows;
  }, [session, runs, baselineReportId]);

  const handleCountClickForRow = useCallback(
    (row: EvaluationHistoryRow, drilldown: MetricCountDrilldown) => {
      if (!session) return;
      if (row.kind === 'tuning' && row.evaluation_run_id) {
        navigate(
          buildTuningDrilldownPath({
            evaluationRunId: row.evaluation_run_id,
            tuningSessionId: session.id,
            drilldown,
          })
        );
        return;
      }
      if (session.baseline_report_id) {
        navigate(
          buildDrilldownPath({
            reportId: session.baseline_report_id,
            drilldown,
          })
        );
      }
    },
    [navigate, session]
  );

  if (!sessionId) {
    return (
      <div className="accuracy-page">
        <div className="accuracy-card accuracy-empty">セッション ID が指定されていません。</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="accuracy-page accuracy-empty" style={{ padding: 48 }}>
        <Loader2 size={32} className="spin" />
        <p style={{ marginTop: 12 }}>セッションを読み込んでいます…</p>
      </div>
    );
  }

  if (!session || !draft || !chatContext || !baselineReportId) {
    return (
      <div className="accuracy-page">
        <Link to="/accuracy" className="accuracy-drilldown-back">
          <ArrowLeft size={16} />
          評価レポート
        </Link>
        <div className="accuracy-card accuracy-empty">セッションが見つかりません。</div>
      </div>
    );
  }

  const completedRuns = runs.filter((r) => r.status === 'completed');
  const isPublished = session.status === 'published';
  const baselineBackLabel = '評価レポート';

  return (
    <div className="accuracy-drilldown-page-shell">
      <div className="accuracy-drilldown-page-main">
        <Link to={buildAccuracyReportDetailPath(baselineReportId)} className="accuracy-drilldown-back">
          <ArrowLeft size={16} />
          {baselineBackLabel}
        </Link>

        <header className="accuracy-drilldown-page-header">
          <h1>{session.name}</h1>
          <p className="accuracy-tuning-session-subtitle">
            <span className="accuracy-flow-badge tuning">チューニング</span>
            {' · '}
            ベースライン: {reportName}
            {' · '}
            本番 v{session.base_guideline_version}
            {isPublished && <> · 公開済み v{session.published_guideline_version}</>}
          </p>
        </header>

        {isPublished && (
          <div className="accuracy-card accuracy-tuning-published-note">
            このセッションは公開済みです。ドラフトの編集・評価実行はできません。
          </div>
        )}

        <div className="accuracy-tuning-session-metrics">
          <AccuracyMetricsSection
            historyRows={sessionHistoryRows}
            onCountClickForRow={handleCountClickForRow}
            sectionDescription="このチューニングセッションの評価結果です。行頭の矢印でカテゴリ別の内訳を展開できます。"
          />
        </div>

        <div className="accuracy-card accuracy-tuning-draft-editor-card">
          <div className="accuracy-tuning-draft-session-bar">
            <div className="accuracy-tuning-session-bar-meta">
              <span className="accuracy-tuning-draft-session-name-label">セッション</span>
              <span className="accuracy-tuning-session-bar-name">{session.name}</span>
              {draftDirty && !isPublished ? (
                <span className="accuracy-tuning-session-unsaved">未保存の変更あり</span>
              ) : null}
            </div>
            <div className="accuracy-tuning-session-bar-actions">
              <button
                type="button"
                className="accuracy-button"
                disabled={!draftDirty || saving || isPublished}
                onClick={() => void handleSaveDraft()}
              >
                {saving ? <Loader2 size={16} className="spin" /> : null}
                ドラフトを保存
              </button>
              <button
                type="button"
                className="accuracy-button primary"
                disabled={running || isPublished}
                onClick={() => void handleRunEvaluation()}
              >
                {running ? <Loader2 size={16} className="spin" /> : <Play size={16} />}
                評価を実行
              </button>
            </div>
          </div>

          <GuidelineDraftEditor
            draft={draft}
            onChange={isPublished ? () => {} : handleDraftChange}
            compareTargets={compareTargets}
            compareTargetId={compareTargetId}
            onCompareTargetChange={setCompareTargetId}
          />
        </div>

        {message && (
          <div className="accuracy-card accuracy-tuning-session-message">{message}</div>
        )}

        {!isPublished && completedRuns.length > 0 && (
          <div className="accuracy-card accuracy-publish-panel">
            <h3 style={{ marginTop: 0 }}>
              <Upload size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              本番ガイドラインへ公開
            </h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              公開する評価実行を手動で選択してください（承認フローなし）。
            </p>
            <label style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
              評価実行
              <select
                className="accuracy-button"
                style={{ display: 'block', width: '100%', marginTop: 6 }}
                value={publishRunId}
                onChange={(e) => setPublishRunId(e.target.value)}
              >
                <option value="">選択…</option>
                {completedRuns.map((r) => (
                  <option key={r.id} value={r.id}>
                    #{r.run_number}（{new Date(r.created_at).toLocaleString('ja-JP')}）
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="accuracy-button primary"
              disabled={!publishRunId}
              onClick={() => void handlePublish()}
            >
              選択した run のスナップショットを公開
            </button>
          </div>
        )}
      </div>

      <AccuracyGuidelineChatSidebar
        context={chatContext}
        onApplyProposal={isPublished ? undefined : handleApplyProposal}
      />
    </div>
  );
};

export default AccuracyTuningSessionPage;
